import { TFunction } from "i18next";

import { MapEditorFeatureList_FeatureFragment } from "./fragments.generated";

type Feature = MapEditorFeatureList_FeatureFragment;

type FeatureGroup = {
    id: string;
    name: string;
    features: Feature[];
};

const TYPE_ORDER: Array<Feature["data"]["__typename"]> = [
    "GqlObservation",
    "GqlPlannerArea",
    "GqlMediaPoint",
    "GqlMapEditorFeatureDataTypeOther",
];

const LABELS: Record<
    Exclude<Feature["data"]["__typename"], "GqlObservation" | "GqlEcologicalFunction">,
    (t: TFunction) => string
> = {
    GqlPlannerArea: t => t("layerType.Areas"),
    GqlMediaPoint: t => t("layerType.MediaPoints"),
    GqlMapEditorFeatureDataTypeOther: t => t("layerType.Other"),
};

export default function groupMapEditorFeatures(features: Feature[], t: TFunction): FeatureGroup[] {
    const obsGroups = new Map<string, FeatureGroup>();
    const ecologicalFunctionGroups = new Map<string, FeatureGroup>();
    const typeGroups = new Map<string, FeatureGroup>();

    for (const feature of features) {
        const typename = feature.data.__typename;

        if (typename === "GqlObservation") {
            const group = obsGroups.get(feature.data.type.id);
            if (group) {
                group.features.push(feature);
            } else {
                obsGroups.set(feature.data.type.id, {
                    id: feature.data.type.id,
                    name: feature.data.type.name,
                    features: [feature],
                });
            }
            continue;
        }

        if (typename === "GqlEcologicalFunction") {
            const group = ecologicalFunctionGroups.get(feature.data.type.id);
            if (group) {
                group.features.push(feature);
            } else {
                ecologicalFunctionGroups.set(feature.data.type.id, {
                    id: feature.data.type.id,
                    name: feature.data.type.name,
                    features: [feature],
                });
            }
            continue;
        }

        const group = typeGroups.get(typename);
        if (group) {
            group.features.push(feature);
        } else {
            typeGroups.set(typename, { id: typename, name: LABELS[typename](t), features: [feature] });
        }
    }

    const orderedObsGroups = [...obsGroups.values()].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
    const orderedEcologicalFunctionGroups = [...ecologicalFunctionGroups.values()].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
    const orderedTypeGroups = TYPE_ORDER.slice(1)
        .map(tn => typeGroups.get(tn))
        .filter((g): g is FeatureGroup => Boolean(g));

    return [...orderedObsGroups, ...orderedEcologicalFunctionGroups, ...orderedTypeGroups];
}
